import { useLazyQuery } from "@apollo/client";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { DrawerSubHeaderText } from "components/primitives/DrawerSubHeaderText";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { HeaderText } from "components/primitives/HeaderText";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useAppDispatch, useAppSelector } from "hooks/utilsHooks/storeHooks";
import parse from "html-react-parser";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { selectQRCode, setQrCode } from "slices/accessSlice";
import { selectIsFridgeEmpty } from "slices/dishesSlice";
import { getFridgeId } from "utils/browser/localStorage";
import { GET_QR_CODE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { selectInitialDataLoaded } from "../../slices/appStateSlice";
import { PlaceCardArrow } from "../PlaceCardArrow";
import { NumberedSteps } from "./nonRfid/NumberedSteps";

type OrderStep1Props = {
	open: boolean;
	toggleDrawer: (newState: boolean) => void;
	/** "qrOnly" is the non-RFID prototype welcome: no badge prompt, the QR as the hero plus numbered steps. */
	variant?: "default" | "qrOnly";
};

const QR_CODE_IPAD_SIZE_SMALL = 180;
const QR_CODE_IPAD_SIZE_LARGE = 300;
const QR_CODE_SAMSUNG_SIZE_SMALL = 140;
const QR_CODE_SAMSUNG_SIZE_LARGE = 260;
// Non-RFID prototype: the QR is the only way in, so it stays scannable even with the drawer closed.
const QR_CODE_HERO_IPAD_SIZE_SMALL = 220;
const QR_CODE_HERO_IPAD_SIZE_LARGE = 320;
const QR_CODE_HERO_SAMSUNG_SIZE_SMALL = 200;
const QR_CODE_HERO_SAMSUNG_SIZE_LARGE = 300;

const getQrCodeSizes = (isIpad: boolean, isQrOnly: boolean) => {
	if (isQrOnly) {
		return isIpad
			? { open: QR_CODE_HERO_IPAD_SIZE_LARGE, closed: QR_CODE_HERO_IPAD_SIZE_SMALL }
			: { open: QR_CODE_HERO_SAMSUNG_SIZE_LARGE, closed: QR_CODE_HERO_SAMSUNG_SIZE_SMALL };
	}
	return isIpad
		? { open: QR_CODE_IPAD_SIZE_LARGE, closed: QR_CODE_IPAD_SIZE_SMALL }
		: { open: QR_CODE_SAMSUNG_SIZE_LARGE, closed: QR_CODE_SAMSUNG_SIZE_SMALL };
};

export const OrderStep1 = ({ open, toggleDrawer, variant = "default" }: OrderStep1Props) => {
	const { t } = useTranslation();
	const qrCode = useAppSelector(selectQRCode);
	const initialDataLoaded = useAppSelector(selectInitialDataLoaded);
	const dispatch = useAppDispatch();
	const swipeableAreaRef = useRef<HTMLDivElement>(null);
	const matches = useMediaQuery("(min-width:1000px)");
	const [getQrCodeQuery, { data: qrCodeData }] = useLazyQuery(GET_QR_CODE_QUERY);
	const isQrOnly = variant === "qrOnly";
	const { open: QrCodeSizeOpen, closed: QrCodeSizeClosed } = getQrCodeSizes(matches, isQrOnly);
	const isFridgeEmpty = useAppSelector(selectIsFridgeEmpty);

	const { trackOrderStep1Screen } = useMixpanelTrackers();
	const hasTrackedRef = useRef(false);

	useEffect(() => {
		if (hasTrackedRef.current) {
			return;
		}
		trackOrderStep1Screen();
		hasTrackedRef.current = true;
		return () => {
			hasTrackedRef.current = false;
		};
	}, [trackOrderStep1Screen]);

	const handleToggleDrawer = () => {
		toggleDrawer(!open);
	};

	useEffect(() => {
		const fetchAndProcessQrCode = async (fridgeId: string) => {
			try {
				logToSentry("QR code is null, fetching new one.", "info");

				const response = await getQrCodeQuery({
					variables: { id: fridgeId },
					fetchPolicy: "network-only",
				});

				const qrCodeData = response.data?.fridge?.access?.qrCode?.code;
				if (!qrCodeData) {
					Sentry.captureException(new Error(`Missing QR code on fridge ${fridgeId}`));
				}
			} catch (error) {
				Sentry.captureException(error);
			}
		};

		if (!qrCode && initialDataLoaded) {
			const fridgeId = getFridgeId();
			if (fridgeId) {
				// biome-ignore lint/complexity/noVoid: explicitly ignore Promise in useEffect
				void fetchAndProcessQrCode(fridgeId);
			}
		}
	}, [qrCode, initialDataLoaded, getQrCodeQuery]);

	useEffect(() => {
		if (qrCodeData?.error) {
			Sentry.captureException(new Error("Could not fetch new QR code"));
		}
		if (qrCodeData?.fridge?.access?.qrCode?.code) {
			dispatch(setQrCode(qrCodeData.fridge.access.qrCode.code));
		}
	}, [qrCodeData, dispatch]);

	if (isFridgeEmpty) {
		return null;
	}

	return (
		<StyledOrderStep1SwipeArea
			open={open}
			orderstep={1}
			sx={{
				display: "flex",
				transition: "all 225ms ease-out",
			}}
			className="swipeable-area"
			ref={swipeableAreaRef}
		>
			<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
			<Box sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
				<StyledPlaceCardBox sx={isQrOnly ? { width: "auto", flex: 1, paddingRight: 3 } : undefined}>
					{isQrOnly ? (
						<>
							<HeaderText variant="cta">{t("order.nonRfid.step1.header")}</HeaderText>
							<DrawerSubHeaderText>{t("order.nonRfid.step1.subheader")}</DrawerSubHeaderText>
							{open && (
								<Box sx={{ marginTop: 2 }}>
									<NumberedSteps
										steps={[
											t("order.nonRfid.step1.steps.1"),
											t("order.nonRfid.step1.steps.2"),
											t("order.nonRfid.step1.steps.3"),
										]}
									/>
								</Box>
							)}
						</>
					) : (
						<>
							<HeaderText variant="cta">{t("order.step1.placeCard")}</HeaderText>
							<PlaceCardArrow isExpanded={open} />
						</>
					)}
				</StyledPlaceCardBox>
				<StyledQrCodeBox>
					{!isQrOnly && (
						<HeaderText variant="cta" align="right">
							{parse(t("order.step1.scanCode"))}
						</HeaderText>
					)}
					{qrCode ? (
						<Box
							onClick={() => handleToggleDrawer()}
							className="test box"
							sx={{
								marginTop: "8px",
							}}
						>
							<img
								src={qrCode}
								alt="QR code for access"
								width={open ? QrCodeSizeOpen : QrCodeSizeClosed}
								height={open ? QrCodeSizeOpen : QrCodeSizeClosed}
								style={{
									transition: "all 0.5s ease",
									borderRadius: "12px",
								}}
							/>
						</Box>
					) : (
						<Skeleton
							width={open ? QrCodeSizeOpen : QrCodeSizeClosed}
							height={open ? QrCodeSizeOpen : QrCodeSizeClosed}
							variant="rectangular"
							role="progressbar"
						/>
					)}
				</StyledQrCodeBox>
			</Box>
		</StyledOrderStep1SwipeArea>
	);
};

const StyledPlaceCardBox = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "50%",

	"& svg": {
		marginTop: theme.spacing(1),
	},
}));

const StyledQrCodeBox = styled(Box)(() => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-end",
}));

const StyledOrderStep1SwipeArea = styled(SwipeableArea, {
	shouldForwardProp: (prop) => prop !== "open",
})(() => ({
	backdropFilter: "blur(10px)",
}));
